import React, { useState } from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import NewBadge from 'src/components/NewBadge/NewBadge';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import { BulkUpdateVisibilityResult } from 'src/services/api/ServiceRequestDashboard/useServiceRequestVisibilityApi';
import {
  UseServiceRequestVisibilityOrchestratorReturn,
  useServiceRequestVisibilityOrchestrator,
} from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/CustomHooks/useServiceRequestVisibilityOrchestrator';
import {
  StepId,
  WIZARD_STEP_PICK,
  WIZARD_STEP_REVIEW,
  WIZARD_STEP_CONFIRMATION,
} from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/CustomHooks/useServiceRequestVisibilityFormState/useServiceRequestVisibilityFormState';
import StepPickServiceRequests from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/StepPickServiceRequests/StepPickServiceRequests';
import StepReviewVisibility from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/StepReviewVisibility/StepReviewVisibility';
import StepVisibilityConfirmation from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/StepVisibilityConfirmation/StepVisibilityConfirmation';
import BulkWizardFooter from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/BulkWizardFooter/BulkWizardFooter';
import './service-request-visibility-dialogue.scss';

const STEP_DESCRIPTIONS: Record<StepId, string> = {
  1: 'Step 1 of 3 — Pick the service requests you want to make internal or external',
  2: 'Step 2 of 3 — Review changes before applying',
  3: 'Step 3 of 3 — All done',
};

export interface ServiceRequestVisibilityDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (result: BulkUpdateVisibilityResult) => void;
}

const ServiceRequestVisibilityDialogue = ({
  isOpen,
  onClose,
  onSuccess,
}: ServiceRequestVisibilityDialogueProps) => {
  const orchestrator: UseServiceRequestVisibilityOrchestratorReturn =
    useServiceRequestVisibilityOrchestrator({ isOpen, onSuccess });

  const [showApplyConfirm, setShowApplyConfirm] = useState<boolean>(false);

  const handleClose = (): void => {
    orchestrator.reset();
    setShowApplyConfirm(false);
    onClose();
  };

  const handleApplyClick = (): void => {
    setShowApplyConfirm(true);
  };

  const handleConfirmApply = async (): Promise<void> => {
    await orchestrator.handleApply();
    setShowApplyConfirm(false);
  };

  const handleCancelApply = (): void => {
    setShowApplyConfirm(false);
  };

  const handleBackToPick = (): void => {
    orchestrator.goToStep(WIZARD_STEP_PICK);
  };

  const isInternalTarget: boolean = orchestrator.targetIsInternalOnly;

  const confirmVariant: 'warning' | 'info' = isInternalTarget
    ? 'warning'
    : 'info';
  const confirmTitle: string = isInternalTarget
    ? 'Make these requests internal?'
    : 'Make these requests external?';
  const confirmMessage: string = isInternalTarget
    ? 'Any selected service requests associated with a contact or assigned to a homeowner will lose that association when they become internal. Homeowners and other external users will no longer be able to see them.'
    : 'Any selected service requests associated with a homeowner will become visible to them once external — the homeowner will be able to see and modify these requests.';
  const confirmActionLabel: string = isInternalTarget
    ? 'Yes, make internal'
    : 'Yes, make external';

  const currentStep: StepId = orchestrator.currentStep;
  const stepDescription: string = STEP_DESCRIPTIONS[currentStep];

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <>
          Manage Service Request Visibility
          <NewBadge spaced />
        </>
      }
      description={stepDescription}
      maxWidth="105rem"
      minHeight="95vh"
      footer={
        <BulkWizardFooter
          currentStep={currentStep}
          canContinue={orchestrator.canContinueFromStep1}
          canApply={orchestrator.canApplyFromStep2}
          isSubmitting={orchestrator.isSubmitting}
          applyLabel="Apply changes"
          onCancel={handleClose}
          onBack={handleBackToPick}
          onContinue={orchestrator.stageSelectedAndContinue}
          onApply={handleApplyClick}
          onDone={handleClose}
        />
      }
    >
      <div className="srvd-body">
        <div className="srvd-body-content">
          {currentStep === WIZARD_STEP_PICK && (
            <StepPickServiceRequests
              projectFilter={orchestrator.projectFilter}
              searchQuery={orchestrator.searchQuery}
              availableProjects={orchestrator.availableProjects}
              filteredServiceRequests={orchestrator.filteredServiceRequests}
              selectedIds={orchestrator.selectedIds}
              isLoadingServiceRequests={orchestrator.isLoadingServiceRequests}
              serviceRequestsError={orchestrator.serviceRequestsError}
              onProjectFilterChange={orchestrator.setProjectFilter}
              onSearchQueryChange={orchestrator.setSearchQuery}
              onToggleSelection={orchestrator.toggleSelection}
              onSetSelectAll={orchestrator.setSelectAll}
            />
          )}
          {currentStep === WIZARD_STEP_REVIEW && (
            <StepReviewVisibility
              stagedServiceRequests={orchestrator.stagedServiceRequests}
              unchangedServiceRequests={orchestrator.unchangedServiceRequests}
              targetIsInternalOnly={orchestrator.targetIsInternalOnly}
              willBecomeInternalCount={orchestrator.willBecomeInternalCount}
              willBecomeExternalCount={orchestrator.willBecomeExternalCount}
              isSubmitting={orchestrator.isSubmitting}
              error={orchestrator.error}
              onSetTarget={orchestrator.setTargetIsInternalOnly}
            />
          )}
          {currentStep === WIZARD_STEP_CONFIRMATION && (
            <StepVisibilityConfirmation
              result={orchestrator.lastApplyResult}
              changedServiceRequests={
                orchestrator.confirmationChangedServiceRequests
              }
              unchangedServiceRequests={orchestrator.unchangedServiceRequests}
              targetIsInternalOnly={orchestrator.targetIsInternalOnly}
            />
          )}
        </div>
      </div>
      {showApplyConfirm && (
        <ConfirmationModal
          variant={confirmVariant}
          title={confirmTitle}
          message={confirmMessage}
          confirmLabel={confirmActionLabel}
          cancelLabel="Cancel"
          isSubmitting={orchestrator.isSubmitting}
          onConfirm={() => void handleConfirmApply()}
          onCancel={handleCancelApply}
        />
      )}
    </GenericDialogue>
  );
};

export default ServiceRequestVisibilityDialogue;